import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";

const ITEMS = [
  { to: 580, suffix: "M+", label: "Media views worldwide" },
  { to: 60, suffix: "K", label: "Clients guided & counting" },
  { to: 160, suffix: "K+", label: "Combined followers" },
  { to: 37, suffix: "K", label: "Email subscribers — 87% open rate" },
  { to: 19, suffix: "K+", label: "Monthly podcast listeners" },
  { to: 10, suffix: "K+", label: "Bestselling copies sold" },
  { to: 5.7, suffix: "M", label: "Website visitors", decimals: 1 },
  { to: 19.9, suffix: "M", label: "Google searches every year", decimals: 1 },
  { to: 78, suffix: "M+", label: "Combined reach of her champions" },
  { to: 30, suffix: "+", label: "Years of professional practice" },
];

function Stat({ to, suffix, label, decimals = 0, i }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      delay: i * 0.12,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(decimals ? v.toFixed(decimals) : String(Math.round(v))),
    });
    return () => controls.stop();
  }, [inView, to, decimals, i]);

  return (
    <div ref={ref} data-testid={`number-stat-${i}`} className="flex flex-col items-center gap-3 py-10 text-center">
      <span className="font-display text-6xl font-light tracking-tight text-[#C5A059] md:text-7xl">
        {val}
        {suffix}
      </span>
      <span className="font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F]/60">
        {label}
      </span>
    </div>
  );
}

export default function Numbers() {
  return (
    <section data-testid="numbers-section" className="relative overflow-hidden bg-[#F7F1E6] py-20 text-[#6B5D4F] lg:py-24">
      <motion.div
        className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(197,160,89,0.15), transparent 65%)" }}
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-end">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
            >
              The Scale
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
              className="mt-5 font-display2 uppercase leading-[0.88] tracking-[0.01em] text-5xl sm:text-6xl lg:text-8xl"
            >
              Built for <br />
              <span className="font-serif2 normal-case italic tracking-normal text-[#C5A059]">prime time.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="max-w-md font-serif2 text-lg italic leading-snug text-[#6B5D4F]/70 md:text-xl"
          >
            Every year the work reaches further — television, bestseller lists, sold-out stages,
            and a community that keeps growing.
          </motion.p>
        </div>
        <div className="mt-14 grid grid-cols-2 border-t border-[#6B5D4F]/10 lg:grid-cols-5">
          {ITEMS.map((s, i) => (
            <Stat key={s.label} {...s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
